import {
  ACTION_FAILURE_HANDLE,
  ACTION_SUCCESS_HANDLE,
  COLLECT_RECEIVED_HANDLE,
  CONDITION_FALSE_HANDLE,
  CONDITION_TRUE_HANDLE,
  FALLBACK_HANDLE,
  INVOKE_COMPLETED_HANDLE,
  LINEAR_HANDLE,
  PALETTE,
  TIMEOUT_HANDLE,
  getCanvasNodePreviewText,
  inferPaletteTypeFromLegacyNode,
  inferWorkflowTypeFromNode,
  isMapTransitionPalette,
  isMessageWorkflowType,
  isWhatsappPalette
} from "./workflowPaletteConstants";

const MAX_STEPS = 80;
const MAX_NODE_VISITS = 56;

const DEMO_VARIABLES = {
  "{{customer_name}}": "Alex",
  "{{user.name}}": "Alex",
  "{{customer_phone}}": "+91 98765 43210",
  "{{order_id}}": "ORD-12845",
  "{{company_name}}": "Prodyt",
  "{{context.userImage.mimeType}}": "image/jpeg",
  "{{context.userImage.id}}": "media-demo-48291",
  "{{context.age}}": "19",
  "{{context.user.age}}": "19"
};

const TERMINAL_WORKFLOW_TYPES = new Set(["END", "HANDOFF"]);

const getOutgoingByHandle = (nodeId, edges) =>
  (edges || [])
    .filter((edge) => edge.source === nodeId && edge.target)
    .reduce((acc, edge) => {
      acc[edge.sourceHandle || LINEAR_HANDLE] = edge.target;
      return acc;
    }, {});

function substituteDemoVariables(text) {
  if (typeof text !== "string" || !text) return "";

  let result = text;
  Object.entries(DEMO_VARIABLES)
    .sort(([a], [b]) => b.length - a.length)
    .forEach(([token, value]) => {
      result = result.replaceAll(token, value);
    });

  result = result.replace(/\{\{([^}]+)\}\}/g, (_, expression) => {
    const token = `{{${expression}}}`;
    if (DEMO_VARIABLES[token]) return DEMO_VARIABLES[token];
    if (/name/i.test(expression)) return "Alex";
    if (/order/i.test(expression)) return "ORD-12845";
    if (/phone/i.test(expression)) return "+91 98765 43210";
    return `[${expression}]`;
  });

  return result;
}

function pushStep(steps, step) {
  if (steps.length >= MAX_STEPS) return false;
  steps.push(step);
  return true;
}

function pushBot(steps, text, buttons = []) {
  const normalized = substituteDemoVariables(text?.trim?.() || "");
  const normalizedButtons = buttons.map((label) => substituteDemoVariables(label)).filter(Boolean);
  if (!normalized && normalizedButtons.length === 0) return true;
  return pushStep(steps, {
    kind: "bot",
    text: normalized || normalizedButtons[0] || "…",
    ...(normalizedButtons.length > 0 ? { buttons: normalizedButtons } : {})
  });
}

function pushUser(steps, text) {
  return pushStep(steps, { kind: "user", text: substituteDemoVariables(text?.trim?.() || "…") });
}

function pushSystem(steps, text) {
  return pushStep(steps, { kind: "system", text });
}

function resolveNodeContext(node) {
  const data = node?.data || {};
  const lookup = {
    paletteType: data.paletteType,
    type: data.workflowType,
    config: data.config,
    conditions: data.conditions,
    action: data.config?.action
  };
  const paletteType = data.paletteType || inferPaletteTypeFromLegacyNode(lookup);
  const workflowType =
    data.workflowType ||
    inferWorkflowTypeFromNode({
      paletteType,
      type: data.workflowType,
      config: data.config
    });

  return { data, paletteType, workflowType };
}

function isTerminalNode(node) {
  const { paletteType, workflowType } = resolveNodeContext(node);
  return TERMINAL_WORKFLOW_TYPES.has(workflowType) || paletteType === PALETTE.END;
}

function formatMessageNode(data) {
  const message = data.config?.message;
  if (!message || typeof message !== "object") {
    const fallback = getCanvasNodePreviewText(data) || data.label || "";
    return { text: fallback, buttons: [] };
  }

  switch (message.type) {
    case "buttons":
      return {
        text: message.text || "",
        buttons: (message.options || []).map((option) => option.label || option.id).filter(Boolean)
      };
    case "list":
      return {
        text: message.text || message.body || "",
        buttons: (message.items || []).map((item) => item.label || item.title || item.id).filter(Boolean)
      };
    case "image":
      return { text: message.caption || message.url || "[Image]", buttons: [] };
    case "video":
      return { text: message.caption || message.url || "[Video]", buttons: [] };
    case "document":
      return {
        text: message.caption || message.filename || message.url || "[Document]",
        buttons: []
      };
    case "template":
      return {
        text: message.body || message.templateId || "[Template message]",
        buttons: []
      };
    case "text":
    default:
      return { text: message.text || message.caption || "", buttons: [] };
  }
}

function formatConditionSummary(conditions = []) {
  if (!conditions.length) return "conditions";
  const first = conditions[0];
  const left = String(first.left || "").replace(/^\$/, "");
  const operator = String(first.operator || "equals").replace(/_/g, " ");
  const right = first.right != null ? String(first.right) : "";
  return `${left} ${operator} ${right}`.trim();
}

/**
 * List every outgoing transition for a node using workflow-type rules.
 */
export function listNodeTransitions(node, nodeId, edges) {
  const { data, paletteType, workflowType } = resolveNodeContext(node);
  const outgoing = getOutgoingByHandle(nodeId, edges);
  const transitions = [];

  const add = (handle, label, targetId, isFallback = false) => {
    if (targetId) transitions.push({ handle, label, targetId, isFallback });
  };

  if (workflowType === "CONDITION") {
    add(CONDITION_TRUE_HANDLE, "Condition met", outgoing[CONDITION_TRUE_HANDLE]);
    add(CONDITION_FALSE_HANDLE, "Condition not met", outgoing[CONDITION_FALSE_HANDLE]);
    return transitions;
  }

  if (workflowType === "COLLECT_INPUT") {
    add(COLLECT_RECEIVED_HANDLE, "Reply", outgoing[COLLECT_RECEIVED_HANDLE]);
    return transitions;
  }

  if (workflowType === "INVOKE_WORKFLOW" || workflowType === "TRANSFER_WORKFLOW") {
    add(INVOKE_COMPLETED_HANDLE, "Completed", outgoing[INVOKE_COMPLETED_HANDLE]);
    return transitions;
  }

  if (workflowType === "ACTION") {
    const actionLabel = data.config?.action || data.label || "action";
    add(ACTION_SUCCESS_HANDLE, `${actionLabel} succeeded`, outgoing[ACTION_SUCCESS_HANDLE]);
    add(ACTION_FAILURE_HANDLE, `${actionLabel} failed`, outgoing[ACTION_FAILURE_HANDLE]);
    return transitions;
  }

  const config = data.config || {};
  if (isMapTransitionPalette(paletteType, config)) {
    const isButtons = /\.buttons(\.|$)/.test(paletteType) ||
      (paletteType === "whatsapp.await" && config?.message?.type === "buttons");
    const rows = isButtons ? config.message?.options || [] : config.message?.items || [];

    rows.forEach((row) => {
      if (!row?.id || !outgoing[row.id]) return;
      add(row.id, row.label || row.title || row.id, outgoing[row.id], row.id === FALLBACK_HANDLE);
    });

    if (outgoing[FALLBACK_HANDLE]) {
      add(FALLBACK_HANDLE, "Other", outgoing[FALLBACK_HANDLE], true);
    }

    return transitions;
  }

  if (isMessageWorkflowType(workflowType) || isWhatsappPalette(paletteType)) {
    const linearTarget =
      outgoing[ACTION_SUCCESS_HANDLE] ||
      outgoing[LINEAR_HANDLE] ||
      outgoing[FALLBACK_HANDLE] ||
      outgoing[COLLECT_RECEIVED_HANDLE];

    if (linearTarget) add(LINEAR_HANDLE, "Continue", linearTarget);
    return transitions;
  }

  const fallbackTarget = Object.entries(outgoing).find(([key]) => key !== TIMEOUT_HANDLE)?.[1];
  if (fallbackTarget) add(LINEAR_HANDLE, "Continue", fallbackTarget);

  return transitions;
}

function measurePathDepth(nodeId, nodeById, edges, memo = new Map(), stack = new Set()) {
  if (!nodeId || stack.has(nodeId)) return 0;
  if (memo.has(nodeId)) return memo.get(nodeId);

  const node = nodeById[nodeId];
  if (!node) return 0;

  if (isTerminalNode(node)) {
    memo.set(nodeId, 0);
    return 0;
  }

  stack.add(nodeId);
  const transitions = listNodeTransitions(node, nodeId, edges).filter(
    (transition) => !(transition.isFallback && transition.targetId === nodeId)
  );

  const depth =
    transitions.length === 0
      ? 1
      : 1 + Math.max(...transitions.map((transition) => measurePathDepth(transition.targetId, nodeById, edges, memo, stack)));

  stack.delete(nodeId);
  memo.set(nodeId, depth);
  return depth;
}

function pickBestTransition(node, nodeId, edges, nodeById) {
  const transitions = listNodeTransitions(node, nodeId, edges).filter(
    (transition) => !(transition.isFallback && transition.targetId === nodeId)
  );

  if (transitions.length === 0) return null;
  if (transitions.length === 1) return transitions[0];

  return [...transitions].sort((left, right) => {
    const depthDelta =
      measurePathDepth(right.targetId, nodeById, edges) - measurePathDepth(left.targetId, nodeById, edges);
    if (depthDelta !== 0) return depthDelta;
    if (left.isFallback !== right.isFallback) return left.isFallback ? 1 : -1;
    return 0;
  })[0];
}

function sampleCollectInputReply(expectedType) {
  switch (expectedType) {
    case "image":
    case "media":
      return "[Image sent]";
    case "video":
      return "[Video sent]";
    case "document":
      return "[Document sent]";
    case "button":
      return "[Button tapped]";
    case "list":
      return "[List option selected]";
    default:
      return "Sample reply";
  }
}

function emitNodeSteps(node, steps) {
  const { data, paletteType, workflowType } = resolveNodeContext(node);

  if (workflowType === "END" || paletteType === PALETTE.END) {
    pushSystem(steps, "Conversation completed.");
    return { terminal: true };
  }

  if (workflowType === "HANDOFF") {
    pushSystem(steps, "Connecting to a support agent…");
    const handoffMessage = data.config?.messageToUser;
    if (handoffMessage) pushBot(steps, handoffMessage);
    return { terminal: true };
  }

  if (isMessageWorkflowType(workflowType) || isWhatsappPalette(paletteType)) {
    const { text, buttons } = formatMessageNode(data);
    pushBot(steps, text, buttons);
    return { requiresBranch: isMapTransitionPalette(paletteType, data.config) };
  }

  if (workflowType === "COLLECT_INPUT") {
    const prompt = data.config?.prompt || getCanvasNodePreviewText(data) || data.label || "Please reply:";
    const expectedType = data.config?.expectedType;
    pushBot(steps, prompt);
    pushUser(steps, sampleCollectInputReply(expectedType));
    return { requiresBranch: false };
  }

  if (workflowType === "ACTION") {
    const actionName = data.config?.action || data.label || "action";
    pushSystem(steps, `Running ${actionName}…`);
    return { requiresBranch: true, branchKind: "action" };
  }

  if (workflowType === "INVOKE_WORKFLOW") {
    const label = data.description || data.config?.workflowId || "sub-flow";
    pushSystem(steps, data.description ? `${label}…` : `Running sub-flow (${label})…`);
    return { requiresBranch: false };
  }

  if (workflowType === "TRANSFER_WORKFLOW") {
    const label = data.description || data.config?.workflowId || "another workflow";
    pushSystem(steps, data.description ? `${label}…` : `Switching to ${label}…`);
    return { requiresBranch: false };
  }

  const preview = getCanvasNodePreviewText(data) || data.description || data.label;
  if (preview) pushBot(steps, preview);
  return { requiresBranch: false };
}

function resolveNextNodeId(node, nodeId, edges, nodeById, { preferHandle } = {}) {
  const outgoing = getOutgoingByHandle(nodeId, edges);
  const { workflowType } = resolveNodeContext(node);

  if (preferHandle && outgoing[preferHandle]) return outgoing[preferHandle];

  if (workflowType === "COLLECT_INPUT") return outgoing[COLLECT_RECEIVED_HANDLE] || null;
  if (workflowType === "INVOKE_WORKFLOW" || workflowType === "TRANSFER_WORKFLOW") {
    return outgoing[INVOKE_COMPLETED_HANDLE] || null;
  }

  const best = pickBestTransition(node, nodeId, edges, nodeById);
  return best?.targetId || null;
}

function walkBranch({
  startId,
  nodeById,
  edges,
  steps,
  visited,
  includeEnd = false,
  stopBeforeEnd = false
}) {
  let currentId = startId;

  while (currentId && steps.length < MAX_STEPS) {
    if (visited.has(currentId)) break;

    const node = nodeById[currentId];
    if (!node) break;

    const { workflowType } = resolveNodeContext(node);
    if (stopBeforeEnd && workflowType === "END") break;

    visited.add(currentId);

    if (workflowType === "CONDITION") {
      const { data } = resolveNodeContext(node);
      pushSystem(steps, `Checking ${formatConditionSummary(data.conditions)}…`);
      const outgoing = getOutgoingByHandle(currentId, edges);
      const trueId = outgoing[CONDITION_TRUE_HANDLE];
      const falseId = outgoing[CONDITION_FALSE_HANDLE];

      if (trueId) {
        walkBranch({ startId: trueId, nodeById, edges, steps, visited, includeEnd: false, stopBeforeEnd });
      }
      if (falseId && steps.length < MAX_STEPS) {
        pushSystem(steps, "If the condition is not met:");
        walkBranch({ startId: falseId, nodeById, edges, steps, visited, includeEnd: false, stopBeforeEnd: true });
      }
      return;
    }

    const emission = emitNodeSteps(node, steps);
    if (emission.terminal) return;

    const outgoing = getOutgoingByHandle(currentId, edges);
    let nextId = null;

    if (emission.requiresBranch) {
      if (emission.branchKind === "action") {
        const successId = outgoing[ACTION_SUCCESS_HANDLE];
        const failureId = outgoing[ACTION_FAILURE_HANDLE];
        nextId = successId || failureId;
        if (successId && failureId && failureId !== successId && steps.length < MAX_STEPS) {
          // success path continues in main walk; failure shown later as alternate
        }
      } else {
        const best = pickBestTransition(node, currentId, edges, nodeById);
        if (best) {
          pushUser(steps, best.label);
          nextId = best.targetId;
        }
      }
    } else {
      nextId = resolveNextNodeId(node, currentId, edges, nodeById);
    }

    if (!nextId) break;
    currentId = nextId;
  }

  if (includeEnd) pushSystem(steps, "Conversation completed.");
}

function collectAlternateBranches(node, nodeId, edges, nodeById, chosenTargetId) {
  return listNodeTransitions(node, nodeId, edges).filter(
    (transition) =>
      transition.targetId &&
      transition.targetId !== chosenTargetId &&
      !(transition.isFallback && transition.targetId === nodeId)
  );
}

function simulatePrimaryPath({ startId, nodeById, edges, steps }) {
  const visited = new Set();
  const branchPoints = [];
  let currentId = startId;
  let visitCount = 0;

  while (currentId && visitCount < MAX_NODE_VISITS && steps.length < MAX_STEPS) {
    if (visited.has(currentId)) {
      pushSystem(steps, "Demo ended (loop detected).");
      break;
    }

    const node = nodeById[currentId];
    if (!node) break;

    // Handle CONDITION before marking visited — walkBranch starts at currentId and
    // must not see it as already visited (common when start = first condition node).
    if (resolveNodeContext(node).workflowType === "CONDITION") {
      walkBranch({ startId: currentId, nodeById, edges, steps, visited, includeEnd: false });
      pushSystem(steps, "Conversation completed.");
      return branchPoints;
    }

    visited.add(currentId);
    visitCount += 1;

    const emission = emitNodeSteps(node, steps);
    if (emission.terminal) break;

    const outgoing = getOutgoingByHandle(currentId, edges);
    let nextId = null;
    let chosenTransition = null;

    if (emission.requiresBranch) {
      if (emission.branchKind === "action") {
        const actionTransitions = listNodeTransitions(node, currentId, edges);
        chosenTransition =
          actionTransitions.find((transition) => transition.handle === ACTION_SUCCESS_HANDLE) ||
          pickBestTransition(node, currentId, edges, nodeById);
        nextId = chosenTransition?.targetId || null;

        const actionAlternates = actionTransitions.filter(
          (transition) => transition.targetId && transition.targetId !== nextId
        );
        if (actionAlternates.length > 0) {
          branchPoints.push({ node, nodeId: currentId, alternates: actionAlternates });
        }
      } else {
        chosenTransition = pickBestTransition(node, currentId, edges, nodeById);
        if (chosenTransition) {
          pushUser(steps, chosenTransition.label);
          nextId = chosenTransition.targetId;

          const alternates = collectAlternateBranches(node, currentId, edges, nodeById, chosenTransition.targetId);
          if (alternates.length > 0) {
            branchPoints.push({ node, nodeId: currentId, alternates });
          }
        }
      }
    } else {
      nextId =
        outgoing[COLLECT_RECEIVED_HANDLE] ||
        outgoing[INVOKE_COMPLETED_HANDLE] ||
        outgoing[ACTION_SUCCESS_HANDLE] ||
        outgoing[LINEAR_HANDLE] ||
        outgoing[FALLBACK_HANDLE] ||
        null;
    }

    if (!nextId) break;
    currentId = nextId;
  }

  if (!steps.some((step) => step.kind === "system" && step.text === "Conversation completed.")) {
    pushSystem(steps, "Conversation completed.");
  }

  return branchPoints;
}

function simulateAlternateBranches(branchPoints, nodeById, edges, steps) {
  branchPoints.forEach(({ node, nodeId, alternates }) => {
    alternates.forEach((transition) => {
      if (steps.length >= MAX_STEPS) return;
      pushSystem(steps, `If the user selects "${transition.label}":`);
      const visited = new Set([nodeId]);
      walkBranch({
        startId: transition.targetId,
        nodeById,
        edges,
        steps,
        visited,
        includeEnd: false,
        stopBeforeEnd: true
      });
    });
  });
}

/**
 * Build a WhatsApp-style demo script by walking the current canvas graph.
 */
export function buildWorkflowDemoScript({ nodes = [], edges = [], start = "", workflowName = "" } = {}) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return {
      steps: [{ kind: "system", text: "Add workflow steps to preview the conversation." }],
      isEmpty: true,
      workflowName
    };
  }

  const nodeById = Object.fromEntries(nodes.map((node) => [node.id, node]));
  const startId = (start && nodeById[start] ? start : nodes[0]?.id) || "";

  if (!startId || !nodeById[startId]) {
    return {
      steps: [{ kind: "system", text: "Set a valid start node to preview this workflow." }],
      isEmpty: true,
      workflowName
    };
  }

  const steps = [];
  const branchPoints = simulatePrimaryPath({ startId, nodeById, edges, steps });
  simulateAlternateBranches(branchPoints, nodeById, edges, steps);

  if (steps.length >= MAX_STEPS) {
    pushSystem(steps, "Demo preview limit reached.");
  }

  if (steps.length === 0) {
    return {
      steps: [{ kind: "system", text: "No previewable conversation steps in this workflow." }],
      isEmpty: true,
      workflowName
    };
  }

  const hasConversationStep = steps.some((step) => step.kind === "bot" || step.kind === "user");
  return {
    steps,
    isEmpty: !hasConversationStep,
    workflowName
  };
}
